/**
 * Execution settings API client — W2-5 / SPEC-04 §3.10.
 *
 * Two scopes, one file, because the card screen has to render both: a card's
 * stepper is meaningless without the account default it falls back to, and the
 * account page is meaningless without the plan cap that bounds it.
 *
 *   GET   /api/account/execution        -> { account: AccountExecution | null }
 *   PATCH /api/account/execution        -> { account, changed }
 *   GET   /api/cards/:id/execution      -> CardExecution
 *   PATCH /api/cards/:id/execution      -> CardExecution
 *   POST  /api/cards/:id/execution/reset-runs -> CardExecution & { ok, changed }
 *
 * `{ account: null }` IS A STATE, NOT AN ERROR: a member working inside somebody
 * else's Workspace owns no account of their own, and the gateway says so with a
 * 200 rather than a 403, so the page renders a named explanation instead of an
 * error boundary.
 *
 * THE THREE FLAGS IN THE PAYLOAD ARE THE SERVER'S, NOT THE BUNDLE'S.
 * `VITE_EXECUTION_SETTINGS` decides whether these screens exist at all;
 * `flags.runCeilingEnforced` / `flags.idleStop` / `flags.slotMetering` say
 * whether the gateway is currently ACTING on the values the screens edit. A
 * surface that hides the difference tells somebody a brake is on when it is not.
 */
import type { HumanRefusalBody } from './billing';
import { apiJson } from './client';

export interface ExecutionServerFlags {
  /** CARD_CEILINGS_ENABLED — is the consecutive-run brake actually applied. */
  runCeilingEnforced: boolean;
  /** IDLE_STOP_ENABLED — is the per-card/account sleep ladder in force. */
  idleStop: boolean;
  /** SLOT_METERING_ENABLED — are slot leases being recorded at all. */
  slotMetering: boolean;
  /**
   * W4-12. SLOT_QUOTAS_ENFORCED or BILLING_ENFORCE_SEATS — is the concurrent-slot
   * CEILING honoured. Not the same question as `slotMetering`: that one is
   * whether leases are recorded, this one is whether the recorded number is ever
   * allowed to stop anything. Both default OFF, so the meter is a measurement
   * and not a budget, and the screen has to say which.
   */
  slotCeilingEnforced: boolean;
}

export interface AccountExecutionSlots {
  /** null = a known plan grants unlimited. */
  quota: number | null;
  /** 0 while `metered` is false — nothing is counting, so nothing is counted. */
  inUse: number;
  alwaysOnQuota: number | null;
  alwaysOnInUse: number;
  metered: boolean;
}

export interface AccountExecution {
  accountId: string;
  /** Machine plan code. Render `planName`. */
  plan: string | null;
  planName: string | null;
  /** false = the account/plan chain did not resolve; every plan limit is null. */
  planResolved: boolean;
  slots: AccountExecutionSlots;
  cardMaxConsecutiveRuns: number;
  maxConsecutiveRunsCap: number | null;
  idleStopMinutes: number | null;
  /** null = the resolved answer is "never sleep". */
  effectiveIdleStopMinutes: number | null;
  flags: ExecutionServerFlags;
}

export interface AccountExecutionPatchInput {
  /**
   * NOT nullable, unlike the card field of the same name. The column is NOT NULL
   * and there is no rung above the account to inherit from; the gateway answers
   * 400 `cannot_clear_account_default` to a null, and this type is what stops the
   * UI from asking.
   */
  cardMaxConsecutiveRuns?: number;
  /** 0 = never sleep, null = inherit the plan, 5..1440 = minutes. */
  idleStopMinutes?: number | null;
}

export interface CardExecution {
  cardId: string;
  /** null = inherit the account default. */
  cardMaxConsecutiveRuns: number | null;
  effectiveCeiling: number;
  consecutiveRuns: number;
  alwaysOn: boolean;
  idleStopMinutes: number | null;
  effectiveIdleStopMinutes: number | null;
  /** null = a known plan grants unlimited pins. */
  maxAlwaysOnSlots: number | null;
  /** null = a known plan grants an unlimited override. */
  maxConsecutiveRunsCap: number | null;
  pinnedAlwaysOnSlots: number;
  /**
   * WHY THE ACCOUNT'S CONDUCTOR IS TAKING NO TURNS — sent only when this card
   * is the Conductor's execution card, the Conductor is switched ON, and
   * `alwaysOn` is off. Both admission fences in the gateway's conductor-control
   * require the pin, while the scheduler's claim does not read it, so that
   * combination is a Conductor that is claimed every poll and refused every
   * poll while every screen says it is on.
   *
   * THE SENTENCE IS THE SERVER'S AND IS RENDERED VERBATIM. Nothing in this app
   * may compose, translate or paraphrase it: the gateway owns the fences, so
   * the gateway owns the words, and a local copy is the copy that would still
   * be claiming this after the fences changed.
   *
   * Absent in every other state — its presence IS the claim.
   */
  conductorStalledReason?: string;
}

export interface CardExecutionPatchInput {
  cardMaxConsecutiveRuns?: number | null;
  alwaysOn?: boolean;
  /** 0 = never sleep, null = inherit the account/plan rung, 5..1440 = minutes. */
  idleStopMinutes?: number | null;
}

export type CardExecutionResetResult = CardExecution & {
  ok: true;
  /** false when the counter was already 0 — the call is idempotent. */
  changed: boolean;
};

/**
 * The WORKSPACE scope — W4-12 / SPEC-09 §3.8.3, from GET /api/orgs/:orgId/execution.
 *
 * A THIRD SCOPE AND NOT A WIDER SECOND ONE. The account payload above answers
 * about the account the caller OWNS. This one answers about ONE Workspace, and
 * on a shared Workspace the paying pool is the Workspace's own — never the
 * personal plan of whoever pressed the button (SPEC-00 A5.6). The two payloads
 * therefore disagree about your slots on purpose, and each says whose slots it
 * is talking about.
 *
 * `enforced` IS THE FIELD THAT KEEPS THIS SCREEN HONEST. It is the gateway's own
 * reading of SLOT_QUOTAS_ENFORCED / BILLING_ENFORCE_SEATS, both absent from
 * production today. False means the ceiling below is a DRY RUN: computed,
 * reported, and stopping nothing. A surface that renders the ceiling without it
 * tells a person a brake is on when it is not.
 */
export type SlotPayerKind = 'account' | 'workspace';

export type SlotCeilingOutcome =
  | 'allowed'
  | 'unlimited'
  | 'comped'
  | 'would_refuse'
  | 'refused'
  | 'unresolved';

export type SlotCeilingSource = 'account_override' | 'workspace_pool' | 'plan' | 'none';

export interface WorkspaceExecution {
  orgId: string;
  /** null = no payer could be selected at all. */
  payer: SlotPayerKind | null;
  /** false = dry run. The ceiling is measured and reported; nothing is stopped. */
  enforced: boolean;
  outcome: SlotCeilingOutcome;
  proceed: boolean;
  /** The gate's own sentence. Rendered as-is. */
  reason: string;
  planName: string | null;
  placeholder: boolean;
  /** null = no ceiling on this plan. Never render it as zero. */
  poolSize: number | null;
  memberCeiling: number | null;
  stewardCeiling: number | null;
  ceilingSource: SlotCeilingSource;
  stewardReserve: number;
  memberOpen: number;
  /** null on the account payer: owner-agent work is not a separate count there. */
  stewardOpen: number | null;
  totalOpen: number;
  /** false = the counts are zeroes nobody took. Render words, not a bar. */
  measured: boolean;
  /** SLOT_METERING_ENABLED — are leases being recorded at all. */
  metered: boolean;
  /** What the gate would say when this pool is full. Built whether armed or not. */
  refusal: HumanRefusalBody | null;
}

export const executionKeys = {
  account: ['execution', 'account'] as const,
  card: (cardId: string) => ['execution', 'card', cardId] as const,
  workspace: (orgId: string) => ['execution', 'workspace', orgId] as const,
};

export const executionApi = {
  getAccount(): Promise<{ account: AccountExecution | null }> {
    return apiJson<{ account: AccountExecution | null }>('/account/execution');
  },
  patchAccount(
    input: AccountExecutionPatchInput,
  ): Promise<{ account: AccountExecution; changed: boolean }> {
    return apiJson<{ account: AccountExecution; changed: boolean }>('/account/execution', {
      method: 'PATCH',
      json: input,
    });
  },
  getWorkspace(orgId: string): Promise<{ workspace: WorkspaceExecution }> {
    return apiJson<{ workspace: WorkspaceExecution }>(`/orgs/${orgId}/execution`);
  },
  getCard(cardId: string): Promise<CardExecution> {
    return apiJson<CardExecution>(`/cards/${cardId}/execution`);
  },
  patchCard(cardId: string, input: CardExecutionPatchInput): Promise<CardExecution> {
    return apiJson<CardExecution>(`/cards/${cardId}/execution`, {
      method: 'PATCH',
      json: input,
    });
  },
  resetCardRuns(cardId: string): Promise<CardExecutionResetResult> {
    return apiJson<CardExecutionResetResult>(`/cards/${cardId}/execution/reset-runs`, {
      method: 'POST',
    });
  },
};

/**
 * "Sleep when idle" choices, shared by the card select and the account select so
 * the two cannot drift into offering different minutes for the same ladder.
 * `null` is inherit and `0` is the NEVER sentinel — migration 0286's two
 * distinct meanings, kept distinct in the control that sets them.
 */
export const IDLE_STOP_CHOICES: ReadonlyArray<{ value: number | null; label: string }> = [
  { value: null, label: 'Inherit' },
  { value: 15, label: '15 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 120, label: '2 hours' },
  { value: 480, label: '8 hours' },
  { value: 0, label: 'Never' },
];

/** The one sentence both screens use for a resolved idle-stop answer. */
export function idleStopSummary(effectiveMinutes: number | null): string {
  if (effectiveMinutes === null) return 'never sleeps';
  if (effectiveMinutes < 60) return `sleeps after ${effectiveMinutes} minutes idle`;
  const hours = effectiveMinutes / 60;
  const rendered = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `sleeps after ${rendered} hour${rendered === '1' ? '' : 's'} idle`;
}
